import React, { useState, useEffect } from "react";
import styles from "./AuthModal.module.css";
import { FaTimes, FaUser, FaEnvelope, FaLock, FaMapMarkerAlt, FaPlus, FaTrash } from "react-icons/fa";
import { useUser } from "../../context/UserContext";
import api from "../../api/axois";
import { useNavigate, useLocation } from "react-router-dom";

const AuthModal = ({ isOpen, onClose, origin }) => {
  const [isLogin, setIsLogin] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [userType, setUserType] = useState("customer"); // "customer" or "kitchen_staff"
  const [formData, setFormData] = useState({
    email: "",
    password: "",
    name: "",
    newAddress: ""
  });
  const { user, setUser } = useUser();
  const [addresses, setAddresses] = useState(user?.addresses || []);
  const navigate = useNavigate();
  const location = useLocation();

  // Sync addresses if user changes
  useEffect(() => {
    if (user?.addresses) {
      setAddresses(user.addresses);
    }
  }, [user]);

  if (!isOpen) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    try {
      let endpoint, payload;
      
      if (isLogin) {
        endpoint = "/chopnation/login";
        payload = { email: formData.email, password: formData.password };
      } else {
        // Determine endpoint based on user type
        if (userType === "kitchen_staff") {
          endpoint = "/chopnation/kitchen/auth/register";
        } else {
          endpoint = "/chopnation/register";
        }
        
        const nameParts = (formData.name || "").trim().split(/\s+/);
        payload = { 
          first_name: nameParts[0] || 'User', 
          last_name: nameParts.slice(1).join(' ') || 'Customer',
          email: formData.email, 
          password: formData.password,
          password_confirmation: formData.password 
        };
      }

      const response = await api.post(endpoint, payload);
      console.log('Auth response:', { endpoint, status: response.status, data: response.data });
      
      const { access_token, user: userData } = response.data;
      
      console.log('Extracted token and user:', { 
        hasToken: !!access_token, 
        tokenLength: access_token?.length,
        userData 
      });

      // Store in localStorage for the interceptor and session persistence
      localStorage.setItem("token", access_token);
      localStorage.setItem("user", JSON.stringify(userData));
      
      // Ensure `role` exists on the client user object. Backend may return different shape
      const derivedRole = userData.role || userData.role_name || (userData.roles && userData.roles[0] && (userData.roles[0].name || userData.roles[0].role)) ||
        // quick fallback for known chef account during dev/testing
        (userData.email && userData.email.includes('chef') ? 'kitchen_staff' : 'customer');

      const clientUser = { ...userData, role: derivedRole };

      // Persist augmented user
      localStorage.setItem('user', JSON.stringify(clientUser));
      setUser(clientUser);
      onClose();

      // Role-based redirects when login originates from another page
      // origin === 'order' means user came from the OrderPage
      if (origin === 'order') {
        if (clientUser.role === 'kitchen_staff') {
          navigate('/kitchen/dashboard');
        } else if (clientUser.role === 'admin' || clientUser.role === 'super_admin') {
          navigate('/admin/BookingManagement');
        } else {
          // regular user: return to order page
          navigate('/dining');
        }
      } else {
        // If no explicit origin, still redirect staff/admin to their dashboards
        if (clientUser.role === 'kitchen_staff') navigate('/kitchen/dashboard');
        if (clientUser.role === 'admin' || clientUser.role === 'super_admin') navigate('/admin/BookingManagement');
      }
    } catch (err) {
      setError(err.response?.data?.message || "Authentication failed. Please check your credentials.");
    } finally {
      setLoading(false);
    }
  };

  const addAddress = () => {
    if (formData.newAddress.trim()) {
      setAddresses([...addresses, formData.newAddress]);
      setFormData({ ...formData, newAddress: "" });
    }
  };

  const removeAddress = (index) => {
    setAddresses(addresses.filter((_, i) => i !== index));
  };

  return (
    <div className={styles.overlay} onClick={onClose}>
      <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
        <button className={styles.closeBtn} onClick={onClose}>
          <FaTimes />
        </button>

        {user ? (
          <div className={styles.profileView}>
            <div className={styles.header}>
              <h2>My Account</h2>
              <p>Manage your delivery details and account info.</p>
            </div>
            
            <div className={styles.userInfo}>
              <div className={styles.avatar}><FaUser /></div>
              <h3>{user.name}</h3>
              <p>{user.email}</p>
            </div>

            <div className={styles.addressSection}>
              <h3><FaMapMarkerAlt /> Saved Addresses</h3>
              <div className={styles.addressList}>
                {addresses.map((addr, index) => (
                  <div key={index} className={styles.addressItem}>
                    <span>{addr}</span>
                    <button type="button" onClick={() => removeAddress(index)} title="Delete"><FaTrash /></button>
                  </div>
                ))}
              </div>
              <div className={styles.addAddressGroup}>
                <input 
                  type="text" 
                  placeholder="Add new delivery address..." 
                  value={formData.newAddress}
                  onChange={(e) => setFormData({...formData, newAddress: e.target.value})}
                />
                <button type="button" onClick={addAddress}><FaPlus /></button>
              </div>
            </div>

            <button className={styles.submitBtn} onClick={onClose}>Save & Close</button>
          </div>
        ) : (
          <>
            <div className={styles.header}>
              <h2>{isLogin ? "Login to Aurelia Dining" : "Join Aurelia Dining"}</h2>
              <p>{isLogin ? "Welcome back! Order your gourmet meal now." : "Create an account to track orders and save addresses."}</p>
            </div>

            <div className={styles.tabs}>
              <button 
                className={isLogin ? styles.activeTab : ""} 
                onClick={() => setIsLogin(true)}
              >
                Login
              </button>
              <button 
                className={!isLogin ? styles.activeTab : ""} 
                onClick={() => setIsLogin(false)}
              >
                Sign Up
              </button>
            </div>

            <form onSubmit={handleSubmit} className={styles.form}>
              {!isLogin && (
                <>
                  <div className={styles.userTypeSelector}>
                    <label>I am a:</label>
                    <div className={styles.typeOptions}>
                      <label>
                        <input 
                          type="radio" 
                          name="userType" 
                          value="customer" 
                          checked={userType === "customer"}
                          onChange={(e) => setUserType(e.target.value)}
                        />
                        Customer
                      </label>
                      <label>
                        <input 
                          type="radio" 
                          name="userType" 
                          value="kitchen_staff" 
                          checked={userType === "kitchen_staff"}
                          onChange={(e) => setUserType(e.target.value)}
                        />
                        Kitchen Staff
                      </label>
                    </div>
                  </div>
                </>
              )}
              
              {!isLogin && (
                <div className={styles.inputGroup}>
                  <FaUser />
                  <input 
                    type="text" 
                    placeholder="Full Name" 
                    required 
                    value={formData.name}
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                  />
                </div>
              )}
              <div className={styles.inputGroup}>
                <FaEnvelope />
                <input 
                  type="email" 
                  placeholder="Email Address" 
                  required 
                  value={formData.email}
                  onChange={(e) => setFormData({...formData, email: e.target.value})}
                />
              </div>
              <div className={styles.inputGroup}>
                <FaLock />
                <input 
                  type="password" 
                  placeholder="Password" 
                  required 
                  value={formData.password}
                  onChange={(e) => setFormData({...formData, password: e.target.value})}
                />
              </div>

              {!isLogin && (
                <div className={styles.addressSection}>
                  <h3><FaMapMarkerAlt /> Initial Address</h3>
                  <div className={styles.addAddressGroup}>
                    <input 
                      type="text" 
                      placeholder="Enter your delivery address..." 
                      value={formData.newAddress}
                      onChange={(e) => setFormData({...formData, newAddress: e.target.value})}
                    />
                  </div>
                </div>
              )}

              {error && <p className={styles.errorText} style={{ color: 'red', fontSize: '14px', marginBottom: '10px' }}>{error}</p>}

              <button type="submit" className={styles.submitBtn} disabled={loading}>
                {loading ? "Processing..." : (isLogin ? "Login" : "Sign Up")}
              </button>
            </form>

            <p className={styles.switchText}>
              {isLogin ? "Don't have an account?" : "Already have an account?"}{" "}
              <span onClick={() => setIsLogin(!isLogin)}>
                {isLogin ? "Sign up here" : "Login here"}
              </span>
            </p>
          </>
        )}
      </div>
    </div>
  );
};

export default AuthModal;
